/**
 * DocCard: card de documento con icono coloreado.
 *
 * Sprint 11 v2 S11-V2-06. Replica `.cb-doc` del handoff
 * (`docs/COMPONENTES.md § Doc`).
 *
 * Variantes de icono según el tipo de documento:
 * - `danger` (fondo rojo suave): protocolos de emergencia.
 * - `info` (fondo azul suave): actas de asamblea, comunicados.
 * - `neutral` (fondo violeta suave): planos.
 * - `success` (fondo verde suave): finanzas, presupuestos.
 */
import type { ReactNode } from 'react';

type IconVariant = 'danger' | 'info' | 'neutral' | 'success';

interface DocCardProps {
  /** SVG/ícono o emoji que va dentro del cuadro coloreado. */
  icon: ReactNode;
  iconVariant: IconVariant;
  title: ReactNode;
  meta?: ReactNode;
  onClick?: () => void;
}

const ICON_BG_CLASSES: Record<IconVariant, string> = {
  danger: 'bg-soft-danger',
  info: 'bg-soft-info',
  neutral: 'bg-soft-neutral',
  success: 'bg-soft-success',
};

const ICON_TEXT_CLASSES: Record<IconVariant, string> = {
  danger: 'text-text-on-danger',
  info: 'text-text-on-info',
  neutral: 'text-text-on-neutral',
  success: 'text-text-on-success',
};

export function DocCard({
  icon,
  iconVariant,
  title,
  meta,
  onClick,
}: DocCardProps) {
  return (
    <div
      className="border-[0.5px] border-white/[0.08] rounded-md py-3 px-4 flex items-center gap-3 cursor-pointer transition-all duration-base hover:border-white/15"
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div
        className={`w-7 h-7 rounded-sm flex items-center justify-center flex-shrink-0 ${ICON_BG_CLASSES[iconVariant]} ${ICON_TEXT_CLASSES[iconVariant]}`}
        aria-hidden="true"
      >
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-base text-text-primary font-medium">{title}</div>
        {meta && <div className="text-sm text-text-muted">{meta}</div>}
      </div>
    </div>
  );
}
